/**
 * Sistema de Scoring v2 — Tipos e interfaces
 *
 * Scoring descompuesto en tres dimensiones independientes:
 *   D1 Origen    → Calidad de la fuente de datos
 *   D2 Precisión → Resolución espacial del resultado
 *   D3 Validación → Verificaciones cruzadas y espaciales
 *
 * Score final = ponderación D1·D2·D3 + flags
 *
 * @module types/scoring
 * @version 2.0.0
 */

import type { GeocodingLevel, GeocodingSource } from './errors/GeocodingErrors';

// ============================================================================
// NIVELES DE CONFIANZA
// ============================================================================

/** Niveles de confianza del scoring v2 */
export type ScoreNivel = 'CRITICA' | 'ALTA' | 'MEDIA' | 'BAJA' | 'NULA';

/** Rangos de puntuación por nivel */
export const SCORE_RANGES: Record<ScoreNivel, [min: number, max: number]> = {
  CRITICA: [90, 100],
  ALTA:    [75, 89],
  MEDIA:   [60, 74],
  BAJA:    [40, 59],
  NULA:    [0, 39],
};

/**
 * Determina el nivel de confianza a partir de un score 0-100
 */
export function getNivel(score: number): ScoreNivel {
  if (score >= 90) return 'CRITICA';
  if (score >= 75) return 'ALTA';
  if (score >= 60) return 'MEDIA';
  if (score >= 40) return 'BAJA';
  return 'NULA';
}

// ============================================================================
// FLAGS DE RESULTADO
// ============================================================================

/**
 * Flags que describen el estado y fiabilidad del resultado.
 * Un resultado puede tener múltiples flags simultáneos.
 */
export type ScoreFlag =
  | 'VALIDADO'                 // ≥2 fuentes coinciden ±50 m
  | 'REVISION_MENOR'           // Pequeña discrepancia, aceptable
  | 'USAR_REFERENCIA'          // Coords de referencia mejores que documento
  | 'REVISION_MANUAL'          // Requiere revisión humana
  | 'GEOCODIFICADO'            // Sin coords originales, geocodificado
  | 'BAJA_CONFIANZA'          // Score < 60, fiabilidad insuficiente
  | 'SIN_COORDENADAS'         // No se pudo obtener ninguna coordenada
  | 'CENTROID_FALLBACK'       // Coordenadas = centroide municipal
  | 'FUENTE_OFFLINE'          // Datos de fuente offline/R2 (no API)
  | 'CROSS_VALIDATED'         // Validación cruzada exitosa (≥2 fuentes)
  | 'SIN_VALIDACION_EXTERNA'  // Cap 65: ninguna fuente externa confirma la coordenada
  | 'FUERA_MUNICIPIO'         // Coordenada fuera del radio tipológico del municipio
  | 'COMPARTE_UBICACION'      // Coordenadas idénticas (≤5 m) a otra infraestructura
                               // del mismo municipio. Coubicación legítima — sin
                               // penalización de score. Badge UI: «Coubicado» (amarillo).
  | 'SUPRAMUNICIPAL_CONFIRMADO'; // Fuera del BBox municipal pero dentro del radio
                               // tipológico (validateSupramunicipal → supramunicipal_valid).
                               // Sustituye a FUERA_MUNICIPIO en esos casos.

// ============================================================================
// D1 — ORIGEN (calidad de la fuente)
// ============================================================================

/**
 * Entrada para cálculo de D1 Origen
 */
export interface D1OrigenInput {
  /** Nivel de cascada usado (L0-L5) */
  cascadeLevel: GeocodingLevel;

  /** Fuente específica que proporcionó el resultado */
  source: GeocodingSource;

  /** ¿Resultado obtenido offline (cache/R2)? */
  offline: boolean;

  /** Geocodificador usado (string descriptivo, e.g. 'specialized:health') */
  geocoderUsed: string;
}

/**
 * Resultado del componente D1 Origen
 */
export interface D1OrigenResult {
  /** Score D1 (0-100) */
  score: number;

  /** Nivel de cascada */
  cascadeLevel: GeocodingLevel;

  /** Fuente usada */
  source: GeocodingSource;

  /** Penalización aplicada por decay de cascada */
  decayApplied: number;

  /** Bonus offline aplicado */
  offlineBonus: number;

  /** Desglose legible para debug/UI */
  detail: string;
}

// ============================================================================
// D2 — PRECISIÓN (resolución espacial)
// ============================================================================

/**
 * Tipo de resolución del match de geocodificación
 */
export type MatchResolution =
  | 'exact'            // Match exacto (portal, coordenada original)
  | 'exact_surveyed'   // Catastro DGC: coordenada levantada in situ (±5 m, SC-CAT-02)
  | 'interpolated'     // Interpolación entre portales
  | 'street'           // Nivel calle (sin portal)
  | 'locality'         // Nivel localidad/entidad
  | 'municipality'     // Nivel municipio (centroide)
  | 'province'         // Nivel provincia
  | 'unknown';         // No determinado

/**
 * Entrada para cálculo de D2 Precisión
 */
export interface D2PrecisionInput {
  /** Tipo de match del geocodificador */
  matchType: string | undefined;

  /** Score de fuzzy matching (0-1), si aplica */
  fuzzyScore: number | undefined;

  /** Resolución inferida del resultado */
  resolution: MatchResolution;

  /** ¿Son coordenadas originales del documento (no geocodificadas)? */
  fromDocument: boolean;
}

/**
 * Resultado del componente D2 Precisión
 */
export interface D2PrecisionResult {
  /** Score D2 (0-100) */
  score: number;

  /** Resolución del match */
  resolution: MatchResolution;

  /** Score de fuzzy aplicado */
  fuzzyPenalty: number;

  /** Bonus por coordenadas de documento */
  documentBonus: number;

  /** Desglose legible */
  detail: string;
}

// ============================================================================
// D3 — VALIDACIÓN (verificaciones cruzadas)
// ============================================================================

/**
 * Entrada para cálculo de D3 Validación
 */
export interface D3ValidacionInput {
  /** ¿Coordenada dentro del municipio declarado? */
  spatialValid: boolean | undefined;

  /** Score de validación cruzada multi-fuente (0-100) */
  crossValidationScore: number | undefined;

  /** Número de fuentes que confirmaron el resultado */
  sourcesConfirming: number;

  /** Discrepancia máxima entre fuentes (metros) */
  maxDiscrepancyMeters: number | undefined;

  /** Confianza de la fuente primaria (0-100). Solo relevante cuando sourcesConfirming === 1. */
  primaryConfidence?: number;
}

/**
 * Resultado del componente D3 Validación
 */
export interface D3ValidacionResult {
  /** Score D3 (0-100) */
  score: number;

  /** Resultado de validación espacial */
  spatialValid: boolean | undefined;

  /** ¿Pasó validación cruzada? */
  crossValidated: boolean;

  /** Número de fuentes externas que confirmaron el resultado */
  sourcesConfirming: number;

  /** Penalización por discrepancia */
  discrepancyPenalty: number;

  /** Bonus por validación espacial */
  spatialBonus: number;

  /** Desglose legible */
  detail: string;
}

// ============================================================================
// SCORE RESULT — Resultado compuesto final
// ============================================================================

/** Pesos por defecto de cada dimensión */
export const DEFAULT_WEIGHTS = {
  d1: 0.35,  // Origen:     35 %
  d2: 0.40,  // Precisión:  40 %
  d3: 0.25,  // Validación: 25 %
} as const;

/**
 * Resultado completo del scoring v2.
 * Incluye las tres dimensiones, score final, nivel y flags.
 */
export interface ScoreResult {
  /** Score final ponderado (0-100) */
  total: number;

  /** Nivel de confianza derivado del score */
  nivel: ScoreNivel;

  /** Componente D1: calidad de la fuente */
  d1: D1OrigenResult;

  /** Componente D2: precisión espacial */
  d2: D2PrecisionResult;

  /** Componente D3: validaciones */
  d3: D3ValidacionResult;

  /** Flags descriptivos del resultado */
  flags: ScoreFlag[];

  /** Pesos usados en la ponderación */
  weights: { d1: number; d2: number; d3: number };

  /** Timestamp ISO de cálculo */
  timestamp: string;
}
